import { cn } from "@/lib/utils";

interface KidsAiLogoProps {
  variant?: "light" | "dark";
  layout?: "horizontal" | "icon";
  animated?: boolean;
  className?: string;
}

/**
 * KidsAI+ brand logo — pure SVG, themeable via brand tokens.
 * - Uses hsl(var(--kid-*)) so colors auto-follow the design system.
 * - `variant="dark"` flips robot screen surfaces to stay readable on dark backgrounds.
 * - `animated` enables blink/bounce/pulse/float motion on robot + confetti.
 */
export const KidsAiLogo = ({
  variant = "light",
  layout = "horizontal",
  animated = false,
  className,
}: KidsAiLogoProps) => {
  const isDark = variant === "dark";

  // Screen surfaces (eyes/mouth grill) — white on light bg, foreground on dark
  const screen = isDark ? "hsl(var(--foreground))" : "hsl(0 0% 100%)";
  const screenOff = isDark ? "hsl(var(--background))" : "hsl(var(--muted))";
  const wordKids = "hsl(var(--kid-purple))";
  const wordAi = "hsl(var(--accent-gold))";
  const plus = "hsl(var(--kid-pink))";
  const tagline = isDark ? "hsl(var(--muted-foreground))" : "hsl(215 16% 35%)";

  const Robot = (
    <g className={animated ? "origin-center" : undefined}>
      {/* Antenna */}
      <line
        x1="60"
        y1="40"
        x2="60"
        y2="22"
        stroke="hsl(var(--kid-purple))"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle
        cx="60"
        cy="18"
        r="5"
        fill="hsl(var(--kid-yellow))"
        className={animated ? "animate-pulse" : undefined}
        style={animated ? { filter: "drop-shadow(0 0 4px hsl(var(--kid-yellow)))" } : undefined}
      />

      {/* Head */}
      <g className={animated ? "animate-bounce-soft" : undefined} style={{ transformOrigin: "60px 60px" }}>
        <rect
          x="28"
          y="36"
          width="64"
          height="50"
          rx="10"
          fill="hsl(var(--kid-purple))"
        />
        {/* Side ears */}
        <rect x="20" y="50" width="10" height="20" rx="3" fill="hsl(var(--kid-blue))" />
        <rect x="90" y="50" width="10" height="20" rx="3" fill="hsl(var(--kid-blue))" />

        {/* Eyes */}
        <g
          className={animated ? "kidsai-blink" : undefined}
          style={{ transformOrigin: "60px 58px" }}
        >
          <circle cx="46" cy="58" r="8" fill={screen} />
          <circle cx="74" cy="58" r="8" fill={screen} />
          <circle cx="46" cy="58" r="3" fill="hsl(var(--kid-blue))" />
          <circle cx="74" cy="58" r="3" fill="hsl(var(--kid-blue))" />
        </g>

        {/* Mouth grill */}
        <rect x="42" y="72" width="36" height="8" rx="2" fill={screen} />
        <line x1="50" y1="72" x2="50" y2="80" stroke={screenOff} strokeWidth="1.5" />
        <line x1="58" y1="72" x2="58" y2="80" stroke={screenOff} strokeWidth="1.5" />
        <line x1="66" y1="72" x2="66" y2="80" stroke={screenOff} strokeWidth="1.5" />
        <line x1="74" y1="72" x2="74" y2="80" stroke={screenOff} strokeWidth="1.5" />
      </g>

      {/* Neck */}
      <rect x="54" y="86" width="12" height="6" fill="hsl(var(--kid-blue))" />

      {/* Body */}
      <rect
        x="22"
        y="92"
        width="76"
        height="32"
        rx="8"
        fill="hsl(var(--kid-purple))"
      />
      {/* Arms */}
      <rect x="10" y="98" width="14" height="20" rx="4" fill="hsl(var(--kid-blue))" />
      <rect x="96" y="98" width="14" height="20" rx="4" fill="hsl(var(--kid-blue))" />

      {/* Chest panel */}
      <rect x="32" y="100" width="56" height="16" rx="4" fill="hsl(var(--kid-blue))" opacity="0.85" />
      {/* Indicator line */}
      <line x1="38" y1="108" x2="76" y2="108" stroke={screen} strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="40" cy="108" r="2" fill="hsl(var(--kid-green))" />
      <circle cx="74" cy="108" r="2" fill="hsl(var(--kid-orange))" />

      {/* Heart */}
      <path
        d="M60 122 l-4 -4 a3 3 0 1 1 4 -4 a3 3 0 1 1 4 4 z"
        fill="hsl(var(--kid-pink))"
      />
    </g>
  );

  if (layout === "icon") {
    return (
      <svg
        viewBox="0 0 120 140"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="KidsAI+"
        className={cn("block", className)}
      >
        <BlinkStyle />
        {Robot}
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 520 160"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="KidsAI+ — Where AI Meets Young Minds"
      className={cn("block", className)}
    >
      <BlinkStyle />

      {/* Robot mascot */}
      <g transform="translate(0, 10)">{Robot}</g>

      {/* Wordmark */}
      <g
        fontFamily="'Bubblegum Sans', 'Fredoka', system-ui, sans-serif"
        fontWeight="700"
      >
        <text x="135" y="86" fontSize="68" fill={wordKids} letterSpacing="-1">
          Kids
        </text>
        <text x="285" y="86" fontSize="68" fill={wordAi} letterSpacing="-1">
          AI
        </text>
        {/* Plus sign */}
        <g
          className={animated ? "kidsai-plus" : undefined}
          style={{ transformOrigin: "395px 56px" }}
        >
          <rect x="385" y="38" width="20" height="6" rx="2" fill={plus} transform="rotate(45 395 56)" />
          <rect x="385" y="38" width="20" height="6" rx="2" fill={plus} transform="rotate(-45 395 56)" />
          <rect x="378" y="53" width="34" height="6" rx="2" fill={plus} />
          <rect x="392" y="39" width="6" height="34" rx="2" fill={plus} />
        </g>
      </g>

      {/* Tagline */}
      <g fontFamily="'Fredoka', system-ui, sans-serif" fontWeight="500">
        <text
          x="135"
          y="118"
          fontSize="14"
          fill={tagline}
          letterSpacing="3"
        >
          WHERE AI MEETS YOUNG MINDS
        </text>
        {/* Tri-color underline bars */}
        <rect x="135" y="128" width="60" height="4" rx="2" fill="hsl(var(--accent-gold))" />
        <rect x="205" y="128" width="80" height="4" rx="2" fill="hsl(var(--kid-red))" />
        <rect x="295" y="128" width="90" height="4" rx="2" fill="hsl(var(--kid-blue))" />
      </g>

      {/* Decorative confetti */}
      <g>
        <circle
          cx="445"
          cy="35"
          r="3"
          fill="hsl(var(--kid-pink))"
          className={animated ? "animate-float" : undefined}
        />
        <polygon
          points="475,28 478,36 486,36 480,41 482,49 475,44 468,49 470,41 464,36 472,36"
          fill="hsl(var(--kid-yellow))"
        />
        <circle
          cx="500"
          cy="55"
          r="2.5"
          fill="hsl(var(--kid-blue))"
          className={animated ? "animate-float" : undefined}
          style={animated ? { animationDelay: "1.2s" } : undefined}
        />
        <circle cx="460" cy="70" r="2" fill="hsl(var(--kid-green))" />
        <circle cx="490" cy="90" r="2.5" fill="hsl(var(--kid-orange))" />
        <circle cx="438" cy="60" r="1.5" fill="hsl(var(--kid-purple))" opacity="0.7" />
        <circle cx="510" cy="110" r="2" fill="hsl(var(--kid-pink))" opacity="0.7" />
      </g>
    </svg>
  );
};

/** Scoped keyframe for eye blink — injected once per SVG render. */
const BlinkStyle = () => (
  <defs>
    <style>{`
      @keyframes kidsai-blink {
        0%, 94%, 100% { transform: scaleY(1); }
        97% { transform: scaleY(0.1); }
      }
      .kidsai-blink { animation: kidsai-blink 6s ease-in-out infinite; transform-box: fill-box; }
      @keyframes kidsai-plus-spin {
        0%, 100% { transform: rotate(0deg) scale(1); }
        50% { transform: rotate(90deg) scale(1.08); }
      }
      .kidsai-plus { animation: kidsai-plus-spin 10s ease-in-out infinite; transform-box: fill-box; }
    `}</style>
  </defs>
);

export default KidsAiLogo;
